import { DateRange } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { CreativeFile, CreativeType } from './Creatives';
import type { campaignSteps } from 'shared/form.tsx';
import { AddressModel } from './AddressFormConfig';
import { FieldArrayPath, FieldArrayWithId, FieldPath } from 'react-hook-form';
import { GeoTargetingData } from './GeoAutocomplete';

export enum CampaignState {
  draft = 'draft',
  ready = 'ready',
}

export enum CampaignStatus {
  active = 'Aktiv',
  draft = 'Entwurf',
  over = 'Beendet',
  ready = 'Bereit - In Prüfung',
  stopped = 'Manuell gestoppt',
}

export enum CampaignFilters {
  ALL = 'alle kampagnen',
  ACTIVE = 'aktive',
  DRAFT = 'entwürfe',
  ENDED_STOPPED = 'beendet/gestoppt',
}

export type TargetingGuidanceConfig = {
  gender: string[];
  age: string[];
  languages: string[];
  interests: string[];
  locations: GeoTargetingData[];
  industry: string[];
  companySize: string[];
};

export type Website = {
  url: string;
  creatives: CreativeFile[];
  dbId?: number;

  /** This is a client-only UI-related flag field */
  isEditing: boolean;

  /** Website-specific targeting guidance in text format */
  targetingGuidance?: string;
  /** Website-specific targeting guidance in JSON format */
  targetingGuidanceConfig?: TargetingGuidanceConfig;
};

export enum BillingMethod {
  ENGAGEMENT = 'CPE',
  CLICK = 'CPC',
}

export type Creative = {
  id: number;
  websiteId: number;
  originalFileName: string;
  creativeType: CreativeType | null;
  url: string;
};

export type CampaignWebsite = {
  id: number;
  url: string;
  targetingGuidance?: string;
  targetingGuidanceConfig?: TargetingGuidanceConfig;
};

export type Campaign = {
  id: number;
  name: string;
  state: CampaignState;
  startDate: string | null;
  endDate: string | null;
  budget: number;

  targetingGuidance: string;
  targetingGuidanceConfig: TargetingGuidanceConfig;
  targetingGuidanceMode: string;
  targetingGuidanceFormat: string;

  websites: Array<CampaignWebsite>;
  creatives: Creative[];

  ga4PropertyId: string;
  ga4ConnectionValid: boolean;
  ga4ConnectionCheckedAt: Date | null;
  cockpitId: number | null;

  billingMethod: BillingMethod;
  maxUnitPrice: number | null;
  effectiveCostPerUnit: number | null;

  billingAddressSalutation: string;
  billingAddressFirstName: string;
  billingAddressLastName: string;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingAddressCountry: string;
  billingAddressPostcode: string;
  billingAddressCity: string;
  billingAddressEmail: string;
  billingAddressPhone: string;
  billingAddressCompanyName: string;
  contactAddressSalutation: string;
  contactAddressFirstName: string;
  contactAddressLastName: string;
  contactAddressLine1: string;
  contactAddressLine2: string;
  contactAddressCountry: string;
  contactAddressPostcode: string;
  contactAddressCity: string;
  contactAddressEmail: string;
  contactAddressPhone: string;
  contactAddressCompanyName: string;

  createdBy: number;

  invoicedAmount: number;
  paidAmount: number;

  createdAt: string;
  updatedAt: string;

  reportId?: string;
  isStopped?: boolean;
  stoppedAt: string | null;
};

export enum CampaignFields {
  name = 'name',
  websites = 'websites',
  campaignPeriod = 'campaignPeriod',
  budget = 'budget',
  billingMethod = 'billingMethod',
  maxUnitPrice = 'maxUnitPrice',
  ga4PropertyId = 'ga4PropertyId',

  targetingGuidance = 'targetingGuidance',
  targetingGuidanceConfig = 'targetingGuidanceConfig',
  targetingGuidanceMode = 'targetingGuidanceMode',
  targetingGuidanceFormat = 'targetingGuidanceFormat',

  billingAddressEqualToContact = 'billingAddressEqualToContact',

  billingAddressSalutation = 'billingAddressSalutation',
  billingAddressFirstName = 'billingAddressFirstName',
  billingAddressLastName = 'billingAddressLastName',
  billingAddressLine1 = 'billingAddressLine1',
  billingAddressLine2 = 'billingAddressLine2',
  billingAddressCountry = 'billingAddressCountry',
  billingAddressPostcode = 'billingAddressPostcode',
  billingAddressCity = 'billingAddressCity',
  billingAddressEmail = 'billingAddressEmail',
  billingAddressPhone = 'billingAddressPhone',
  billingAddressCompanyName = 'billingAddressCompanyName',

  contactAddressSalutation = 'contactAddressSalutation',
  contactAddressFirstName = 'contactAddressFirstName',
  contactAddressLastName = 'contactAddressLastName',
  contactAddressLine1 = 'contactAddressLine1',
  contactAddressLine2 = 'contactAddressLine2',
  contactAddressCountry = 'contactAddressCountry',
  contactAddressPostcode = 'contactAddressPostcode',
  contactAddressCity = 'contactAddressCity',
  contactAddressEmail = 'contactAddressEmail',
  contactAddressPhone = 'contactAddressPhone',
  contactAddressCompanyName = 'contactAddressCompanyName',
}

export type CampaignContactAddress = AddressModel<'contactAddress'>;
export type CampaignContactBillingAddress = AddressModel<'billingAddress'>;

export type CampaignFormInputs = {
  [CampaignFields.name]: string;
  [CampaignFields.websites]: Website[];
  [CampaignFields.targetingGuidance]: string;
  [CampaignFields.targetingGuidanceConfig]: TargetingGuidanceConfig;
  [CampaignFields.campaignPeriod]: DateRange<DateTime>;
  [CampaignFields.budget]: number | null;
  [CampaignFields.billingMethod]: BillingMethod;
  [CampaignFields.maxUnitPrice]: number | null;
  [CampaignFields.ga4PropertyId]: string;
  [CampaignFields.targetingGuidanceMode]: string;
  [CampaignFields.targetingGuidanceFormat]: string;

  [CampaignFields.billingAddressEqualToContact]: boolean;
} & CampaignContactAddress &
  CampaignContactBillingAddress;

export type CampaignFormField<F extends FieldArrayPath<CampaignFormInputs> = FieldArrayPath<CampaignFormInputs>> =
  FieldArrayWithId<CampaignFormInputs, F>;

export enum CampaignStepState {
  Complete = 'complete',
  Warning = 'warning',
  Wrong = 'wrong',
  Default = 'default',
}

export enum CampaignStep {
  Basic = 'Basic',
  Creatives = 'Creatives',
  Targeting = 'Targeting',
  KPI = 'KPI',
  GA = 'GA',
  Complete = 'Complete',
}

/** Represents a single step descriptor: `CampaignStep` type and related fields */
export type StepDescriptor<Step extends CampaignStep, Fields extends FieldPath<CampaignFormInputs>[]> = {
  step: Step;
  fields: Fields;
  title: string;
  subtitle: string;
  label: string;
};

export type CampaignFormStepData<
  Step extends CampaignStep = CampaignStep,
  Fields extends FieldPath<CampaignFormInputs>[] = FieldPath<CampaignFormInputs>[],
> = StepDescriptor<Step, Fields> & {
  state: CampaignStepState;
  isValid?: boolean;
};

/** Retrieves `CampaignStep` discriminants from the step descriptors and return an array of them */
export type OrderedSteps<StepDescriptors extends readonly StepDescriptor<any, any>[]> = {
  [I in keyof StepDescriptors]: StepDescriptors[I]['step'];
};

/** Specifies a map of steps by `CampaignStep` */
export type StepMap<StepDescriptors extends readonly StepDescriptor<any, any>[]> = {
  [Desc in StepDescriptors[number] as Desc['step']]: CampaignFormStepData<Desc['step'], Desc['fields']>;
};

/** Returns a `Record<CampaignStep, number>` where the value is the index of the step */
export type StepIndices<StepDescriptors extends readonly StepDescriptor<any, any>[]> = Record<
  StepDescriptors[number]['step'],
  number
>;

export type CampaignFormStepDataMap = typeof campaignSteps;
export type StepStates = Record<CampaignStep, CampaignStepState>;
export type StepStatesByCampaignId = Record<number, StepStates>;

export type CampaignTargetingSuggestion = {
  websiteUrl: string;
  ready: boolean;
  suggestions: string[];
};
export type CampaignTargetingSuggestions = {
  suggestions: CampaignTargetingSuggestion[];
};
